import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { isTokenValid, isTokenValidForOrg, type StudioToken } from '@/utils/tokenUtils'

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

let store: any = null
const getStore = async () => {
  if (!store) {
    const mod = await import('@/store')
    store = mod.store
  }
  return store
}

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token?: string | null) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

axios.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig

    // Check if error is due to an unauthorized request
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If a token refresh is already in progress, queue the request
        return new Promise<string | null | undefined>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            return axios(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Resolve the store once before accessing any state or dispatching
        const vuexStore = await getStore()

        // Obtain tokens from the Vuex store
        const JLoToken = vuexStore.state.session.JLoToken
        const studioToken = vuexStore.state.session.studioToken

        // Refresh JLoToken if invalid
        if (!isTokenValid(JLoToken)) {
          const hasJLoToken = await vuexStore.dispatch('session/ensureValidJLoToken')
          if (!hasJLoToken) {
            throw new Error('Unable to ensure valid JLo token')
          }

          if (!vuexStore.state.session.selectedOrganisation?.id) {
            throw new Error('No organisation selected, cannot restore studio token')
          }
          await vuexStore.dispatch('session/restoreStudioToken')
        } else if (isTokenValid(JLoToken) && (!isTokenValid(studioToken) || !isTokenValidForOrg(studioToken))) {
          // Refresh StudioToken if invalid
          if (!vuexStore.state.session.selectedOrganisation?.id) {
            throw new Error('No organisation selected, cannot restore studio token')
          }
          await vuexStore.dispatch('session/restoreStudioToken')
        }

        const localStorageStdToken = window.localStorage.getItem('studio_token')
        let studioTokenLocalStorage: StudioToken | null = null
        try {
          studioTokenLocalStorage = localStorageStdToken ? JSON.parse(localStorageStdToken) : null
        } catch {
          studioTokenLocalStorage = null
        }

        if (!studioTokenLocalStorage?.access_token) {
          throw new Error('No valid studio token available')
        }

        // Set the Authorization header with new token
        originalRequest.headers['X-Studio-Authorization'] = `Bearer ${studioTokenLocalStorage.access_token}`

        // After obtaining new token(s)
        processQueue(null, studioTokenLocalStorage?.access_token)

        // Retry the original request once tokens have been updated
        return axios(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError)
        const vuexStore = await getStore()
        vuexStore.dispatch('session/logout')
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    } else {
      // Reject if not a 401 error or token refresh fails
      return Promise.reject(error)
    }
  }
)

export default axios
